import React, { useState, useEffect, useCallback } from 'react';
import {
    Users, AlertTriangle, Loader2, Pencil, Trash2, UserCheck,
    Shield, CheckCircle, XCircle, Save, Code2, Palette, Megaphone
} from 'lucide-react';
import axiosService from './services/axiosService';
import Modal from './components/Modal';
import Card from './components/Card';
import Button from './components/Button';
import Input from './components/Input';
import { API_BASE_URL as API_BASE } from './config/api';

export default function UsersAdminPage() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showCreate, setShowCreate] = useState(false);
    const [editUser, setEditUser] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState('');

    const emptyForm = { email: '', password: '', first_name: '', last_name: '', user_type: 'developer', is_active: true };
    const [form, setForm] = useState(emptyForm);

    const USER_TYPE_OPTIONS = [
        { value: 'admin', label: 'Admin', icon: Shield, color: 'text-accent', desc: 'Accès complet à toute la plateforme' },
        { value: 'developer', label: 'Développeur', icon: Code2, color: 'text-blue-600', desc: 'Dashboard, Projets, Documentation' },
        { value: 'designer', label: 'Designer', icon: Palette, color: 'text-purple-600', desc: 'Dashboard, Projets, Documentation' },
        { value: 'growth_marketing', label: 'Growth Marketing', icon: Megaphone, color: 'text-emerald-600', desc: 'Dashboard, Projets, Documentation' },
        { value: 'commercial', label: 'Commercial', icon: UserCheck, color: 'text-green-600', desc: 'Dashboard, Prospects' },
    ];

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axiosService.get(`${API_BASE}/api/public/admin/users`);
            setUsers(res.data);
        } catch (e) {
            setError(e?.response?.data?.detail || 'Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchUsers(); }, [fetchUsers]);

    const openCreate = () => { setForm(emptyForm); setFormError(''); setShowCreate(true); setEditUser(null); };
    const openEdit = (u) => {
        setForm({ email: u.email, password: '', first_name: u.first_name, last_name: u.last_name, user_type: u.user_type || 'developer', is_active: u.is_active });
        setFormError('');
        setEditUser(u);
        setShowCreate(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setFormError('');
        try {
            if (editUser) {
                const payload = { first_name: form.first_name, last_name: form.last_name, user_type: form.user_type, is_active: form.is_active };
                if (form.password) payload.password = form.password;
                await axiosService.patch(`${API_BASE}/api/public/admin/users/${editUser.id}`, payload);
            } else {
                await axiosService.post(`${API_BASE}/api/public/admin/users`, form);
            }
            setShowCreate(false);
            setEditUser(null);
            await fetchUsers();
        } catch (e) {
            setFormError(e?.response?.data?.detail || 'Une erreur est survenue');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteConfirm) return;
        setSaving(true);
        try {
            await axiosService.delete(`${API_BASE}/api/public/admin/users/${deleteConfirm.id}`);
            setDeleteConfirm(null);
            await fetchUsers();
        } catch (e) {
            setError(e?.response?.data?.detail || 'Erreur lors de la suppression');
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (u) => {
        try {
            await axiosService.patch(`${API_BASE}/api/public/admin/users/${u.id}`, { is_active: !u.is_active });
            await fetchUsers();
        } catch (e) {
            setError(e?.response?.data?.detail || 'Erreur');
        }
    };

    return (
        <div className="mx-auto w-full px-2 md:px-0 flex flex-col gap-6">
            {/* En-tête */}
            <div>
                <h1 className="font-heading text-2xl font-bold text-blue-gray-900 mb-1">Utilisateurs</h1>
                <p className="text-blue-gray-400 text-sm mb-4">Gestion des comptes utilisateurs et des droits d'administration.</p>
            </div>

            {/* Hero */}
            <div className="rounded-2xl border border-blue-gray-100 bg-white/80 p-6 md:p-8 shadow-sm flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h2 className="font-heading text-lg font-bold text-blue-gray-900 mb-1">Comptes utilisateurs</h2>
                    <p className="text-blue-gray-400 text-sm">Créez, modifiez ou supprimez des comptes. Gérez les droits d'administration.</p>
                </div>
                <Button variant="primary" size="sm" onClick={openCreate} className="flex items-center gap-2 flex-shrink-0">
                    <UserCheck size={16} />
                    Nouvel utilisateur
                </Button>
            </div>

            {/* Erreur globale */}
            {error && (
                <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 flex items-center gap-2">
                    <AlertTriangle size={16} className="flex-shrink-0" />
                    {error}
                </div>
            )}

            {/* Tableau */}
            <Card className="overflow-hidden p-0">
                {loading ? (
                    <div className="flex items-center justify-center py-16 text-blue-gray-400 gap-2">
                        <Loader2 size={20} className="animate-spin" />
                        <span>Chargement…</span>
                    </div>
                ) : users.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-blue-gray-400 gap-2">
                        <Users size={32} strokeWidth={1.2} />
                        <p className="text-sm">Aucun utilisateur trouvé</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-blue-gray-100 bg-blue-gray-50/60">
                                    <th className="text-left font-semibold text-blue-gray-500 py-3 px-4">Utilisateur</th>
                                    <th className="text-left font-semibold text-blue-gray-500 py-3 px-4">Email</th>
                                    <th className="text-left font-semibold text-blue-gray-500 py-3 px-4">Rôle</th>
                                    <th className="text-center font-semibold text-blue-gray-500 py-3 px-4">Actif</th>
                                    <th className="text-left font-semibold text-blue-gray-500 py-3 px-4">Inscription</th>
                                    <th className="py-3 px-4"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-blue-gray-50">
                                {users.map(u => (
                                    <tr key={u.id} className="hover:bg-blue-gray-50/40 transition-colors">
                                        <td className="py-3 px-4">
                                            <div className="flex items-center gap-3">
                                                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-accent/10 text-accent font-bold text-sm flex-shrink-0">
                                                    {(u.first_name?.[0] || u.email[0]).toUpperCase()}
                                                </span>
                                                <span className="font-medium text-blue-gray-800">
                                                    {u.first_name} {u.last_name}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="py-3 px-4 text-blue-gray-600">{u.email}</td>
                                        <td className="py-3 px-4">
                                            {u.user_type === 'admin' && (
                                                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-accent/10 text-accent">
                                                    <Shield size={11} /> Admin
                                                </span>
                                            )}
                                            {u.user_type === 'developer' && (
                                                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                                    <Code2 size={11} /> Développeur
                                                </span>
                                            )}
                                            {u.user_type === 'designer' && (
                                                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                                    <Palette size={11} /> Designer
                                                </span>
                                            )}
                                            {u.user_type === 'growth_marketing' && (
                                                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                                                    <Megaphone size={11} /> Growth Marketing
                                                </span>
                                            )}
                                            {(!u.user_type || u.user_type === 'commercial') && u.user_type === 'commercial' && (
                                                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                                    <UserCheck size={11} /> Commercial
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-3 px-4 text-center">
                                            <button
                                                onClick={() => toggleActive(u)}
                                                title={u.is_active ? 'Désactiver' : 'Activer'}
                                                className={`inline-flex items-center justify-center w-7 h-7 rounded-full transition ${u.is_active ? 'bg-green-50 text-green-600 hover:bg-red-50 hover:text-red-500' : 'bg-blue-gray-100 text-blue-gray-400 hover:bg-green-50 hover:text-green-600'}`}
                                            >
                                                {u.is_active ? <CheckCircle size={14} /> : <XCircle size={14} />}
                                            </button>
                                        </td>
                                        <td className="py-3 px-4 text-blue-gray-400 text-xs">
                                            {u.date_joined ? new Date(u.date_joined).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                        </td>
                                        <td className="py-3 px-4">
                                            <div className="flex items-center justify-end gap-1">
                                                <button
                                                    onClick={() => openEdit(u)}
                                                    className="p-1.5 text-blue-gray-400 hover:text-accent hover:bg-accent/10 rounded-lg transition"
                                                    title="Modifier"
                                                >
                                                    <Pencil size={14} />
                                                </button>
                                                <button
                                                    onClick={() => setDeleteConfirm(u)}
                                                    className="p-1.5 text-blue-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                                                    title="Supprimer"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {/* Modal créer / modifier */}
            <Modal
                open={showCreate}
                onClose={() => { setShowCreate(false); setEditUser(null); }}
                title={editUser ? "Modifier l'utilisateur" : 'Nouvel utilisateur'}
            >
                <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-2">
                    {!editUser && (
                        <div>
                            <label className="block text-xs font-semibold text-blue-gray-600 mb-1">Email *</label>
                            <Input
                                type="email"
                                value={form.email}
                                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                                required
                                placeholder="prenom@exemple.com"
                                className="w-full"
                            />
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-semibold text-blue-gray-600 mb-1">Prénom *</label>
                            <Input
                                type="text"
                                value={form.first_name}
                                onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))}
                                required={!editUser}
                                placeholder="Prénom"
                                className="w-full"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-blue-gray-600 mb-1">Nom *</label>
                            <Input
                                type="text"
                                value={form.last_name}
                                onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))}
                                required={!editUser}
                                placeholder="Nom"
                                className="w-full"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-blue-gray-600 mb-1">
                            {editUser ? 'Nouveau mot de passe (laisser vide pour ne pas modifier)' : 'Mot de passe *'}
                        </label>
                        <Input
                            type="password"
                            value={form.password}
                            onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                            required={!editUser}
                            placeholder={editUser ? '••••••••' : 'Mot de passe'}
                            className="w-full"
                            autoComplete="new-password"
                        />
                    </div>
                    {/* Sélecteur de rôle */}
                    <div>
                        <label className="block text-xs font-semibold text-blue-gray-600 mb-2">Rôle *</label>
                        <div className="grid grid-cols-1 gap-2">
                            {USER_TYPE_OPTIONS.map(opt => {
                                const Icon = opt.icon;
                                const selected = form.user_type === opt.value;
                                return (
                                    <label key={opt.value} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${selected ? 'border-accent/60 bg-accent/5' : 'border-blue-gray-200 hover:border-blue-gray-300'}`}>
                                        <input type="radio" name="user_type" value={opt.value} checked={selected}
                                            onChange={() => setForm(f => ({ ...f, user_type: opt.value }))} className="sr-only" />
                                        <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${selected ? 'bg-accent/10' : 'bg-blue-gray-100'}`}>
                                            <Icon size={14} className={selected ? 'text-accent' : 'text-blue-gray-400'} />
                                        </span>
                                        <div className="flex-1">
                                            <div className={`text-sm font-medium ${selected ? 'text-accent' : 'text-blue-gray-800'}`}>{opt.label}</div>
                                            <div className="text-xs text-blue-gray-400">{opt.desc}</div>
                                        </div>
                                        {selected && <CheckCircle size={16} className="text-accent flex-shrink-0" />}
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-blue-gray-700">
                        <input
                            type="checkbox"
                            checked={form.is_active}
                            onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
                            className="w-4 h-4 accent-accent"
                        />
                        <CheckCircle size={14} className="text-green-500" />
                        Compte actif
                    </label>
                    {formError && (
                        <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 flex items-center gap-2">
                            <AlertTriangle size={14} className="flex-shrink-0" />
                            {formError}
                        </div>
                    )}
                    <div className="flex justify-end gap-2 pt-2">
                        <Button type="button" variant="secondary" onClick={() => { setShowCreate(false); setEditUser(null); }}>
                            Annuler
                        </Button>
                        <Button type="submit" variant="primary" disabled={saving} className="flex items-center gap-2">
                            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                            {editUser ? 'Enregistrer' : 'Créer'}
                        </Button>
                    </div>
                </form>
            </Modal>

            {/* Modal confirmation suppression */}
            <Modal
                open={!!deleteConfirm}
                onClose={() => setDeleteConfirm(null)}
                title="Supprimer l'utilisateur"
            >
                <div className="flex flex-col gap-4 mt-2">
                    <p className="text-sm text-blue-gray-700">
                        Êtes-vous sûr de vouloir supprimer <strong>{deleteConfirm?.first_name} {deleteConfirm?.last_name}</strong> ({deleteConfirm?.email}) ?
                        Cette action est irréversible et supprimera également tous ses projets.
                    </p>
                    <div className="flex justify-end gap-2">
                        <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>Annuler</Button>
                        <button
                            type="button"
                            onClick={handleDelete}
                            disabled={saving}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-medium transition disabled:opacity-50"
                        >
                            {saving ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                            Supprimer
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
